import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { setDisposition, listConfirmedClasses, suggestClassTarget } from './api';

// Where this class's content will be filed.
//
// This was a nullable field whose form defaulted to "Revise existing class" with an empty
// picker, so an unmade decision looked like a made one, and nothing required it until
// write-back - the last step, after the class had been built, reviewed, priced and sold.
//
// JPRO now derives a target from the content-match verdict, so this panel's job changed:
// it presents a proposal with the reason behind it and asks a person to agree. Confirming
// is what the development gate requires.
export default function ClassTargetPanel({ concept, conceptId, onChanged }) {
  const c = concept;
  const target = c.class_target || {};
  const derived = Boolean(target.derived);
  const confirmed = Boolean(target.confirmed);

  const { data: confirmedData } = useQuery({
    queryKey: ['training-confirmed-classes'], queryFn: listConfirmedClasses,
  });
  const confirmedClasses = confirmedData?.items || confirmedData?.classes || [];

  const [mode, setMode] = React.useState(target.disposition === 'revision' ? 'revision' : 'new');
  const [targetId, setTargetId] = React.useState(target.zu_content_item_id ? String(target.zu_content_item_id) : '');
  const [name, setName] = React.useState(target.disposition === 'revision' ? '' : (target.name || ''));

  // Reseed when the concept changes OR when the stored target itself changes.
  //
  // Keying on the concept id alone left the form stale: "Re-match the catalog" re-derives an
  // unconfirmed target for the SAME concept, so the summary above updated from props while
  // the inputs still held the old target - and saving would then confirm the target JPRO had
  // already replaced.
  //
  // Ordinary typing is safe because these deps are the server's values: they compare equal
  // across a background refetch, so the effect does not run and does not clobber the field.
  React.useEffect(() => {
    setMode(target.disposition === 'revision' ? 'revision' : 'new');
    setTargetId(target.zu_content_item_id ? String(target.zu_content_item_id) : '');
    setName(target.disposition === 'revision' ? '' : (target.name || ''));
  }, [c.id, target.disposition, target.zu_content_item_id, target.name]);

  // The way out of "Not set yet." Every concept predating the class-target work has no
  // target and nothing automatic will give it one: derive! runs at generation and on a
  // rematch only, and the rematch route names a new class after its marketing title because
  // it has no concept package to draw a catalog name from.
  const suggest = useMutation({
    mutationFn: () => suggestClassTarget(conceptId),
    onSuccess: () => { toast.success('JPRO worked out a class target - review it and confirm'); onChanged(); },
    onError: (e) => toast.error(e?.message || 'JPRO could not work out a class target'),
  });

  const save = useMutation({
    mutationFn: () => setDisposition(conceptId, mode === 'revision'
      ? { disposition: 'revision', zu_content_item_id: targetId }
      : { disposition: 'new', target_class_name: name }),
    onSuccess: () => { toast.success('Class target confirmed'); onChanged(); },
    onError: (e) => toast.error(e?.message || 'Could not set the class target'),
  });

  const chosenClass = confirmedClasses.find((k) => String(k.id) === String(targetId));
  // Unchanged from what is stored means the author is agreeing with JPRO rather than
  // editing, so the button says so. Same endpoint either way.
  const unchanged = mode === (target.disposition || 'new')
    && (mode === 'revision'
      ? String(targetId) === String(target.zu_content_item_id || '')
      : name.trim() === (target.name || '').trim());
  const canSave = mode === 'revision' ? Boolean(targetId) : Boolean(name.trim());

  // A revision JPRO chose is the one target that can do real harm: it points this class's
  // content into an existing class's folder and updates that class instead of creating one.
  // So it warns rather than notes, and it names the class by name.
  const risky = mode === 'revision' && (target.risky || (derived && !confirmed));

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-[#7d8aa0]">Class target</div>
          <div className="text-xs text-[#c7d0e0] mt-1">
            {target.disposition
              ? (target.disposition === 'revision'
                ? <>Revises <b className="text-white">{target.name || 'an existing class'}</b></>
                : <>Creates a new class named <b className="text-white">{target.name}</b></>)
              : 'Not set yet.'}
          </div>
        </div>
        <span className={`text-[10px] px-2 py-1 rounded shrink-0 ${confirmed
          ? 'bg-[#0b2a17] text-green-400 border border-[#1c4a30]'
          : 'bg-[#3a2a12] text-[#f7a23b] border border-[#6b4516]'}`}>
          {confirmed ? '✓ Confirmed' : 'Needs confirming'}
        </span>
      </div>

      {/* Why JPRO chose this. Without it, confirming is agreeing to something unexplained. */}
      {target.reason && (
        <div className="text-[11px] text-[#8a95a8] bg-[#0f1620] border border-[#1d2634] rounded-lg px-3 py-2">
          <span className="text-[#5ea9ff]">JPRO Intelligence:</span> {target.reason}
          {/* A first suggestion can be poor, and until now there was no way to ask again.
              Re-asking clears any confirmation, because what comes back is a guess again. */}
          <button onClick={() => suggest.mutate()} disabled={suggest.isPending}
            className="block mt-1.5 text-[11px] text-[#5ea9ff] disabled:opacity-40">
            {suggest.isPending ? 'Working it out…' : 'Ask JPRO to work it out again'}
          </button>
        </div>
      )}

      {confirmed && target.confirmed_by && (
        <div className="text-[10px] text-[#5f6b7e]">
          Confirmed by {target.confirmed_by}
          {target.confirmed_at ? ` on ${new Date(target.confirmed_at).toLocaleDateString()}` : ''}
        </div>
      )}

      {!confirmed && (
        <div className="text-[11px] text-[#f7a23b]">
          ⚠ Development cannot be approved until this is confirmed. It decides which folder
          the finished class is filed into, so JPRO will not act on its own guess.
        </div>
      )}

      {/* Nothing set at all: the primary move is to let JPRO work it out, rather than
          leaving the author to guess a folder name from a marketing title. */}
      {!target.disposition && (
        <div className="rounded-lg border border-[#2b3a4d] bg-[#0f1620] px-3 py-3">
          <div className="text-[11px] text-[#c7d0e0] mb-2">
            This concept has no class target yet. JPRO can work one out: it checks the
            catalog for a class this revises, and if there is none it names the new class.
          </div>
          <button onClick={() => suggest.mutate()} disabled={suggest.isPending}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#1f6feb] text-white disabled:opacity-40">
            {suggest.isPending ? 'JPRO is working it out…' : 'Ask JPRO to work it out'}
          </button>
          <div className="text-[10px] text-[#5f6b7e] mt-2">
            You still confirm the result, or set it by hand below.
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 text-xs text-[#c7d0e0]">
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} /> New class
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === 'revision'} onChange={() => setMode('revision')} /> Revise an existing class
        </label>
      </div>

      {mode === 'revision' ? (
        <select value={targetId} onChange={(e) => setTargetId(e.target.value)}
          className="w-full bg-[#0f1620] border border-[#3a4a5f] rounded px-2 py-1.5 text-xs text-[#c7d0e0] [&>option]:bg-[#0f1620]">
          <option value="">Select the class to revise</option>
          {confirmedClasses.map((k) => (
            <option key={k.id} value={k.id}>{k.title}{k.program ? ` (${k.program})` : ''}</option>
          ))}
        </select>
      ) : (
        <div>
          <input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Class name"
            className="w-full bg-[#0f1620] border border-[#3a4a5f] rounded px-2 py-1.5 text-xs text-[#c7d0e0]" />
          <div className="text-[10px] text-[#5f6b7e] mt-1">
            This becomes the class folder name in the content library, so keep it short and
            plain. JPRO suggested one from the concept; change it if it does not fit.
          </div>
        </div>
      )}

      {/* Destructive-action warning, proportional to what confirming actually does rather
          than to how confident the match score looked. */}
      {risky && (
        <div className="text-[11px] bg-[#3a1d1d] border border-[#5c2b2b] text-[#ffb4b4] rounded-lg px-3 py-2 space-y-1">
          <div className="font-semibold">Read this before confirming</div>
          <div>
            JPRO picked this revision target from a content match, not a person. Confirming
            it files this class&apos;s content into{' '}
            <b>{chosenClass?.title || target.name || 'that existing class'}</b> and updates
            that class instead of creating a new one.
          </div>
          <div>
            If that is the wrong class, its content is what changes. Check the class name
            above, or switch to <b>New class</b>.
          </div>
        </div>
      )}

      <button
        onClick={() => {
          if (risky) {
            const label = chosenClass?.title || target.name || 'that existing class';
            if (!window.confirm(
              `Confirm revising "${label}"?\n\nThis class's content will be filed into that existing class and update it, not create a new class. If that is the wrong class, its content is what changes.`
            )) return;
          }
          save.mutate();
        }}
        disabled={save.isPending || !canSave}
        className={`text-xs px-3 py-1.5 rounded-lg text-white disabled:opacity-40 ${
          risky ? 'bg-[#8a2c2c]' : 'bg-[#1f6feb]'}`}>
        {save.isPending ? 'Saving…'
          : unchanged && !confirmed ? 'Confirm this target'
          : confirmed && unchanged ? 'Re-confirm'
          : 'Save class target'}
      </button>
    </div>
  );
}
